/** Shared A4 preview canvas constants + height measurement for resume iframes. */

import { PREVIEW_WIDTH_PX } from './resume-preview-document';

export const PREVIEW_FALLBACK_HEIGHT_PX = 1123;
export { PREVIEW_WIDTH_PX } from './resume-preview-document';
export const PREVIEW_HEIGHT_BUFFER_PX = 4;
export const PREVIEW_MIN_SCALE = 0.35;
export const PREVIEW_HEIGHT_RETRY_MS = [150, 500, 1200] as const;

export function getPreviewRootElement(doc: Document): HTMLElement | null {
  const candidate =
    doc.getElementById('template-section') ||
    doc.querySelector('.template-section') ||
    doc.querySelector('.resume-container') ||
    doc.querySelector('.t40-container');
  return candidate instanceof HTMLElement
    ? candidate
    : doc.body instanceof HTMLElement
      ? doc.body
      : null;
}

function elementContentBottom(el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  const style = el.ownerDocument.defaultView
    ? el.ownerDocument.defaultView.getComputedStyle(el)
    : null;
  const marginBottom = style ? Number.parseFloat(style.marginBottom) || 0 : 0;
  return rect.bottom + marginBottom;
}

/**
 * Measure full preview document height including table bodies and trailing
 * content (absolute/negative-margin decorations can still under-report; we
 * take the max of several signals).
 */
export function measurePreviewDocumentHeight(doc: Document): number {
  const root = getPreviewRootElement(doc);
  const rootHeight = root
    ? Math.max(root.scrollHeight, root.getBoundingClientRect().height, root.offsetHeight)
    : 0;

  let tableBodyHeight = 0;
  if (root) {
    const bodies = root.querySelectorAll('.template-body, table');
    for (let i = 0; i < bodies.length; i++) {
      const node = bodies[i];
      if (node instanceof HTMLElement) {
        tableBodyHeight = Math.max(
          tableBodyHeight,
          node.scrollHeight,
          node.getBoundingClientRect().height,
          node.offsetHeight,
        );
      }
    }
  }

  let lastChildBottom = 0;
  if (root && root.lastElementChild instanceof HTMLElement) {
    const rootTop = root.getBoundingClientRect().top;
    lastChildBottom = Math.ceil(elementContentBottom(root.lastElementChild) - rootTop);
  }

  let descendantBottom = 0;
  if (root) {
    const rootTop = root.getBoundingClientRect().top;
    const descendants = root.querySelectorAll('*');
    for (let i = 0; i < descendants.length; i++) {
      const node = descendants[i];
      if (node instanceof HTMLElement) {
        descendantBottom = Math.max(
          descendantBottom,
          Math.ceil(elementContentBottom(node) - rootTop),
        );
      }
    }
  }

  const docElementHeight = doc.documentElement
    ? Math.max(doc.documentElement.scrollHeight, doc.documentElement.offsetHeight)
    : 0;
  const bodyHeight = doc.body ? Math.max(doc.body.scrollHeight, doc.body.offsetHeight) : 0;

  return (
    Math.ceil(
      Math.max(
        rootHeight,
        tableBodyHeight,
        lastChildBottom,
        descendantBottom,
        docElementHeight,
        bodyHeight,
        PREVIEW_FALLBACK_HEIGHT_PX,
      ),
    ) + PREVIEW_HEIGHT_BUFFER_PX
  );
}

export function computePreviewScale(columnWidth: number, gutterPx = 48): number {
  const availableWidth = Math.max(0, columnWidth - gutterPx);
  const scale = availableWidth / PREVIEW_WIDTH_PX;
  return Math.min(1, Math.max(PREVIEW_MIN_SCALE, scale));
}
